import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, money } from '../lib/api';
import { messageFor } from '../lib/session';
import { Empty, Field, Loading, Notice } from '../components/ui';

type Customer = {
  id: string; legal_name: string; billing_email: string; primary_contact_name: string | null;
  project_count: number; outstanding_cents: number;
};

export function CustomerList() {
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      void api.get<{ customers: Customer[] }>(`/api/admin/customers${query}`)
        .then((result) => setCustomers(result.customers)).catch((cause) => setError(messageFor(cause)));
    }, 180);
    return () => clearTimeout(timer);
  }, [search]);

  async function createCustomer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError(null);
    try {
      const result = await api.post<{ id: string }>('/api/admin/customers', {
        name: form.get('name'), billingEmail: form.get('email'), contactName: form.get('contactName') || null,
        contactPhone: form.get('contactPhone') || null,
        addressLine1: form.get('addressLine1') || null,
        addressLine2: form.get('addressLine2') || null,
        city: form.get('city') || null,
        state: form.get('state') || null,
        postalCode: form.get('postalCode') || null,
        country: String(form.get('country') || 'US').toUpperCase(),
      });
      navigate(`/admin/customers/${result.id}`);
    } catch (cause) { setError(messageFor(cause)); }
  }

  return (
    <>
      <header className="simple-page-head"><div><span>WebForGrowth owner</span><h1>Customers</h1><p>Create customers, then manage each project, agreement, and payment.</p></div><button onClick={() => setShowForm((value) => !value)}>{showForm ? 'Close' : 'Add customer'}</button></header>
      {error && <Notice kind="error">{error}</Notice>}
      {showForm && (
        <section className="simple-panel simple-form-panel">
          <h2>New customer</h2>
          <form onSubmit={createCustomer}>
            <div className="simple-form-grid">
              <Field label="Customer / company name"><input name="name" required /></Field>
              <Field label="Billing email"><input name="email" type="email" required /></Field>
              <Field label="Contact name"><input name="contactName" /></Field>
              <Field label="Contact phone"><input name="contactPhone" /></Field>
            </div>
            <h3 className="form-subhead">Billing address</h3>
            <p className="form-subnote">Printed on every invoice and receipt, and sent to the payment provider.</p>
            <div className="simple-form-grid">
              <Field label="Address line 1"><input name="addressLine1" autoComplete="address-line1" /></Field>
              <Field label="Address line 2"><input name="addressLine2" autoComplete="address-line2" /></Field>
              <Field label="City"><input name="city" autoComplete="address-level2" /></Field>
              <Field label="State / region"><input name="state" autoComplete="address-level1" /></Field>
              <Field label="Postal code"><input name="postalCode" autoComplete="postal-code" /></Field>
              <Field label="Country" hint="Two-letter code, e.g. US"><input name="country" defaultValue="US" maxLength={2} autoComplete="country" /></Field>
            </div>
            <div className="form-submit"><button type="submit">Create customer</button></div>
          </form>
        </section>
      )}
      <div className="list-toolbar"><input aria-label="Search customers" placeholder="Search customers" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
      <section className="simple-panel">
        {!customers ? <Loading /> : customers.length === 0 ? <Empty title="No customers yet">Add your first customer.</Empty> : (
          <div className="customer-list">
            {customers.map((customer) => (
              <Link to={`/admin/customers/${customer.id}`} key={customer.id}>
                <div><strong>{customer.legal_name}</strong><span>{customer.billing_email}</span></div>
                <dl><div><dt>Projects</dt><dd>{customer.project_count}</dd></div><div><dt>Outstanding</dt><dd>{money(customer.outstanding_cents)}</dd></div></dl>
                <b>Open <span aria-hidden="true">&rarr;</span></b>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
